import { FirestoreReducerDataState } from '@store/firestore/firestore.reducer';
import { Registrant, RegistrantStates } from '@core/models/registrant';
import { FIRESTORE_COLLECTION_REGISTRANTS } from '@store/firestore/allFirestoreCollections';
import { registrantDataState } from '@store/registrants';
import { MemoizedSelector, createSelector, Store } from '@ngrx/store';
import { removeDiacritics } from '@shared/helpers';
import { FirestoreCollectionReducer } from '@store/firestore/firestore-collection.reducer';
import { ModifiedDataAction } from '@store/firestore/firestore.actions';

export interface RegistrantDataState extends FirestoreReducerDataState<Registrant> {}

export class RegistrantReducer extends FirestoreCollectionReducer<Registrant, RegistrantDataState> {
  private static instance: RegistrantReducer;
  collectionName = FIRESTORE_COLLECTION_REGISTRANTS;
  featureSelector = registrantDataState;

  INNER_COLLECTION_ID = 'idCardNumber';

  ADD_ACTION = '[Registrants] Add registrant';
  UPSERT_ACTION = '[Registrants] Upsert registrant';
  DELETE_ACTION = '[Registrants] Delete registrant';
  MODIFY_ACTION = '[Registrants] Modify registrant';

  ADDED_ACTION = '[Registrants] Added registrant from firebase';
  DELETED_ACTION = '[Registrants] Deleted registrant from firebase';
  MODIFIED_ACTION = '[Registrants] Modified registrant from firebase';
  UPSERTED_ACTION = '[Registrants] Upserted registrant firebase';
  MODIFIED_LOCALLY_ACTION = '[Registrants] Modify locally registrant';

  static getInstance() {
    if (!RegistrantReducer.instance) {
      RegistrantReducer.instance = new RegistrantReducer();
    }
    return RegistrantReducer.instance;
  }

  getFieldsToRemove(): string[] {
    const fieldsToRemove = super.getFieldsToRemove();
    fieldsToRemove.push('selected', 'eventMailList');
    return fieldsToRemove;
  }

  initializeFieldsToRemove(registrant: Registrant): Registrant {
    registrant.selected = false;
    registrant.eventMailList = [];

    return registrant;
  }

  ////////////////////////////////////////////////SHOW SELECTORS////////////////////////////////////////////////

  /**Selector in charge of extracting an array with all registrants per show  */
  getRegistrantsByShowIdSelector(showId: string): MemoizedSelector<object, Registrant[]> {
    const CUSTOM_ID = `SHOW_${showId}_REGISTRANTS`;
    const selectorToCreate = createSelector(this.getConvertToArraySelector(), allRegistrants =>
      allRegistrants.filter(registrant => registrant.eventId === showId)
    );

    return this.getOrCreateSelectorById(CUSTOM_ID, selectorToCreate);
  }

  /**Selector in charge of extracting an array with all registrants without assigned ticket per show  */
  getInvitedByShowIdSelector(showId: string): MemoizedSelector<object, Registrant[]> {
    const CUSTOM_ID = `SHOW_${showId}_INVITED_REGISTRANTS`;
    const selectorToCreate = createSelector(this.getRegistrantsByShowIdSelector(showId), allRegistrants =>
      allRegistrants.filter(
        registrant => registrant.actualState && registrant.actualState.type === RegistrantStates.INVITED
      )
    );

    return this.getOrCreateSelectorById(CUSTOM_ID, selectorToCreate);
  }

  /**Selector in charge of extracting an array with all registrants without assigned ticket per show  */
  getNotGoingByShowIdSelector(showId: string): MemoizedSelector<object, Registrant[]> {
    const CUSTOM_ID = `SHOW_${showId}_NOT_GOING_REGISTRANTS`;
    const selectorToCreate = createSelector(this.getRegistrantsByShowIdSelector(showId), allRegistrants =>
      allRegistrants.filter(
        registrant => registrant.actualState && registrant.actualState.type === RegistrantStates.NOT_GOING
      )
    );

    return this.getOrCreateSelectorById(CUSTOM_ID, selectorToCreate);
  }

  /**Selector in charge of extracting an array with all registrants with assigned ticket per show  */
  getTicketAssignedByShowIdSelector(showId: string): MemoizedSelector<object, Registrant[]> {
    const CUSTOM_ID = `SHOW_${showId}_TICKET_ASSIGNED_REGISTRANTS`;
    // const selectorToCreate = createSelector(this.getRegistrantsByShowIdSelector(showId), allRegistrants =>
    const selectorToCreate = createSelector(this.getRegistrantsByShowIdSelector(showId), allRegistrants =>
      allRegistrants.filter(
        registrant => registrant.actualState && registrant.actualState.type === RegistrantStates.REGISTERED
      )
    );

    return this.getOrCreateSelectorById(CUSTOM_ID, selectorToCreate);
  }

  /**Selector in charge of extracting an array with all attendees per show  */
  getAttendeesByShowIdSelector(showId: string): MemoizedSelector<object, Registrant[]> {
    const registrantsByShowIdSelector: MemoizedSelector<object, Registrant[]> = this.getRegistrantsByShowIdSelector(
      showId
    );
    const CUSTOM_ID = `SHOW_${showId}_ATTENDEES`;
    const selectorToCreate = createSelector(registrantsByShowIdSelector, registrantsByShowId =>
      registrantsByShowId.filter(registrant => registrant.status === 1)
    );

    return this.getOrCreateSelectorById(CUSTOM_ID, selectorToCreate);
  }

  /**Selector in charge of extracting an array with all selected registrants per show  */
  getSelectedRegistrantsByShowIdSelector(showId: string): MemoizedSelector<object, Registrant[]> {
    const CUSTOM_ID = `SHOW_${showId}_SELECTED_REGISTRANTS`;
    const selectorToCreate = createSelector(this.getRegistrantsByShowIdSelector(showId), allRegistrants =>
      allRegistrants.filter(registrant => registrant.selected)
    );

    return this.getOrCreateSelectorById(CUSTOM_ID, selectorToCreate);
  }

  //SEARCH

  getRegistrantsByShowIdSearchSelector(
    text: string,
    selector: MemoizedSelector<object, Registrant[]>
  ): MemoizedSelector<object, Registrant[]> {
    text = removeDiacritics(text);
    const regex = new RegExp(`${text.toLowerCase()}`);
    const selectorToCreate = createSelector(selector, allRegistrants => {
      return allRegistrants.filter(registrant => {
        const fullName = removeDiacritics(registrant.fullName.toLowerCase());

        let matched = !!fullName.match(regex);

        if (!matched && registrant.email) {
          matched = !!removeDiacritics(registrant.email.toLowerCase()).match(regex);
        }

        return matched;
      });
    });

    return selectorToCreate;
  }

  ////////////////////////////////////////////////END SHOW SELECTORS////////////////////////////////////////////////
}

export function oneRegistrantChangeSelectStatus(store: Store<any>, registrant: Registrant, selected: boolean): void {
  const registrantsReducer = RegistrantReducer.getInstance();

  const innerRegistrant = { ...registrant, selected };

  const actionType = registrantsReducer.MODIFIED_LOCALLY_ACTION;
  store.dispatch(new ModifiedDataAction(actionType, innerRegistrant.id, innerRegistrant));
}

export function multipleRegistrantChangeSelectStatus(store: Store<any>, registrants: Registrant[], selected: boolean) {
  registrants
    .filter(registrant => registrant.selected !== selected)
    .forEach(registrant => oneRegistrantChangeSelectStatus(store, registrant, selected));
}
